import StyledBooleanCheckbox from '@/components/styled/StyledBooleanCheckbox';
import Modal from '@/components/ui/Modal';
import {
  useGetCircuitsForAgencies,
  useRemoveDistributor,
  useShareCircuitsWithAgencies,
} from '@/hooks/useDistributors';
import { RowSelectionState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import CircuitSelectionTable from './CircuitSelectionTable';
import Loader from '@/components/ui/Loader';
import { Distributor } from '@/types/distributors/distributorColumnDef';
import StyledButton from '@/components/styled/StyledButton';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import { useToast } from '@/hooks/useToast';
import ModalDeletion from '@/components/ui/ModalDeletion';
import { useTranslation } from '@/i18n';

interface UpdateSharedCircuitsModalProps {
  distributors: Distributor[];
  closeModal: () => void;
}

const UpdateSharedCircuitsModal = ({
  distributors,
  closeModal,
}: UpdateSharedCircuitsModalProps) => {
  const { t } = useTranslation();
  const { control } = useForm<{
    shouldShareAllCurrentCircuits: boolean;
    shouldShareAllFutureCircuits: boolean;
  }>({
    mode: 'onChange',
    defaultValues: {
      shouldShareAllCurrentCircuits: false,
      shouldShareAllFutureCircuits: distributors.every(
        (distributor) => distributor.shouldShareAllFutureCircuits,
      ),
    },
  });

  const shouldShareAllCurrentCircuits = useWatch({
    control,
    name: 'shouldShareAllCurrentCircuits',
  });
  const shouldShareAllFutureCircuits = useWatch({
    control,
    name: 'shouldShareAllFutureCircuits',
  });

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const [isDeletionModalOpen, setIsDeletionModalOpen] = useState(false);

  const selectedCircuits = useMemo(
    () => Object.keys(rowSelection),
    [rowSelection],
  );

  const {
    data: circuitsResponse,
    isSuccess,
    isLoading,
    isFetching,
  } = useGetCircuitsForAgencies(
    distributors.map((distributor) => distributor.agencyId),
  );

  const { mutateAsync: shareCircuitsWithAgencies } =
    useShareCircuitsWithAgencies();

  const { mutateAsync: removeDistributor } = useRemoveDistributor();

  const { toastAsync } = useToast();

  return (
    <>
      <Modal
        title={
          distributors.length === 1
            ? distributors[0].distributor.name
            : `Agences sélectionnées (${distributors.length})`
        }
        actionOnClick={() => {
          toastAsync(
            shareCircuitsWithAgencies({
              circuitIds: selectedCircuits,
              distributorIds: distributors.map(
                (distributor) => distributor.agencyId,
              ),
              shouldShareAllCurrentCircuits,
              shouldShareAllFutureCircuits,
              circuitsToRemoveIds:
                circuitsResponse?.circuits
                  ?.filter((circuit) => circuit._count.sharedCircuits > 0)
                  .map((circuit) => circuit.id)
                  .filter(
                    (circuitId) => !selectedCircuits.includes(circuitId),
                  ) ?? [],
            }),
            { success: t('distributors.modals.updateSharedCircuits.success') },
          );
        }}
        closeModal={closeModal}
        actionTitle={t('distributors.modals.updateSharedCircuits.actionTitle')}
        headerNode={
          distributors.length === 1 && (
            <StyledButton
              title={t(
                'distributors.modals.updateSharedCircuits.removeFromList',
              )}
              className="bg-amber-600 hover:bg-amber-700 border-amber-700 py-2 px-3"
              onClick={() => {
                setIsDeletionModalOpen(true);
              }}
              icon={<TrashIcon className="w-5 h-5 stroke-white" />}
            />
          )
        }
      >
        {isLoading || isFetching ? (
          <Loader />
        ) : (
          isSuccess && (
            <div className="flex flex-col gap-8">
              <div className="flex gap-4 w-full">
                <div className="w-full">
                  <StyledBooleanCheckbox
                    control={control}
                    name="shouldShareAllCurrentCircuits"
                    optionLabel={t(
                      'distributors.modals.circuitSelection.shareAllCurrentCircuits',
                    )}
                    className="text-gray-700"
                  />
                </div>
                <div className="w-full">
                  <StyledBooleanCheckbox
                    control={control}
                    name="shouldShareAllFutureCircuits"
                    optionLabel={t(
                      'distributors.modals.circuitSelection.shareAllFutureCircuits',
                    )}
                    className="text-gray-700"
                  />
                </div>
              </div>
              <CircuitSelectionTable
                filteredCircuits={circuitsResponse.circuits}
                agencies={distributors.map(
                  (distributor) => distributor.agencyId,
                )}
                setSelectedCircuitRows={setRowSelection}
                shouldSelectAll={shouldShareAllCurrentCircuits}
              />
            </div>
          )
        )}
      </Modal>
      {isDeletionModalOpen && (
        <ModalDeletion
          actionOnClick={() => {
            toastAsync(removeDistributor(distributors[0].agencyId), {
              success: t('modal.deletion.message.single.distributeur'),
              error: t('distributors.distributorDeletionError'),
            });
            closeModal();
          }}
          closeModal={() => setIsDeletionModalOpen(false)}
          type="distributeur"
        />
      )}
    </>
  );
};

export default UpdateSharedCircuitsModal;
